import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  IconButton,
  Input,
  Text,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  useToast,
  Badge,
  Spinner,
  SimpleGrid,
  Icon,
  ModalCloseButton,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiSmile, FiMeh, FiFrown } from 'react-icons/fi';
import { useTags, Tag } from '../../contexts/TagsContext';
import axios from 'axios';
import env from 'config/env';
import { format } from 'date-fns';
import { useEncryption } from '../../contexts/EncryptionContext';
import { useJournal } from '../../contexts/JournalContext';

interface TagFormData {
  name: string;
  color: string;
}

interface JournalEntry {
  id: string;
  encrypted_content: string;
  created_at: string;
  mood_score?: number;
  mood_label?: string;
  is_favorite: boolean;
  tags?: string[];
}

interface TagWithCount extends Tag {
  entryCount: number;
}

const TagsPage = () => {
  const { tags, createTag, updateTag, deleteTag } = useTags();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [formData, setFormData] = useState<TagFormData>({ name: '', color: '#000000' });
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<TagWithCount | null>(null);
  const [tagEntries, setTagEntries] = useState<JournalEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [tagsWithCount, setTagsWithCount] = useState<TagWithCount[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const toast = useToast();
  const tagBgColor = useColorModeValue('white', 'gray.800');
  const { entries } = useJournal();
  const { decrypt } = useEncryption();
  const [decryptedPreviews, setDecryptedPreviews] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchTagCounts = async () => {
      try {
        const response = await axios.get<Array<{ tag_id: string; count: number }>>(`${env.API_URL}/tags/counts`);
        const counts = new Map(response.data.map(item => [item.tag_id, item.count]));
        const tagsWithCounts: TagWithCount[] = tags.map(tag => ({
          ...tag,
          entryCount: counts.get(tag.id) || 0
        }));
        setTagsWithCount(tagsWithCounts);
      } catch (error) {
        console.error('Error fetching tag counts:', error);
      }
    };

    if (tags.length > 0) {
      fetchTagCounts();
    }
  }, [tags]);

  useEffect(() => {
    const loadPreviews = async () => {
      const previews: Record<string, string> = {};
      for (const entry of entries) {
        try {
          const decryptedContent = await decrypt(entry.encrypted_content);
          previews[entry.id] = decryptedContent.substring(0, 100) + '...';
        } catch (error) {
          console.error('Error decrypting entry:', error);
          previews[entry.id] = 'Error decrypting entry';
        }
      }
      setDecryptedPreviews(previews);
    };

    loadPreviews();
  }, [entries, decrypt]);

  const fetchTagEntries = async (tagId: string) => {
    setLoadingEntries(true);
    try {
      const response = await axios.get(`${env.API_URL}/journal/by-tag/${tagId}`);
      setTagEntries(response.data);
    } catch (error) {
      toast({
        title: 'Error fetching entries',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoadingEntries(false);
    }
  };

  const handleOpenModal = (tag?: TagWithCount) => {
    if (tag) {
      setFormData({ name: tag.decrypted_name || '', color: tag.color || '#000000' });
      setEditingTagId(tag.id);
    } else {
      setFormData({ name: '', color: '#000000' });
      setEditingTagId(null);
    }
    onOpen();
  };

  const handleTagClick = async (tag: TagWithCount) => {
    setSelectedTag(tag);
    await fetchTagEntries(tag.id);
  };

  const handleSubmit = async () => {
    try {
      if (editingTagId) {
        await updateTag(editingTagId, formData.name, formData.color);
        toast({
          title: 'Tag updated',
          status: 'success',
          duration: 2000,
        });
      } else {
        await createTag(formData.name, formData.color);
        toast({
          title: 'Tag created',
          status: 'success',
          duration: 2000,
        });
      }
      onClose();
    } catch (error) {
      // Error is handled by the context
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this tag?')) {
      try {
        await deleteTag(id);
        toast({
          title: 'Tag deleted',
          status: 'success',
          duration: 2000,
        });
      } catch (error) {
        // Error is handled by the context
      }
    }
  };

  const handleEntryClick = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setEntryModalOpen(true);
  };

  return (
    <Container maxW="container.lg" py={8}>
      <Flex justify="space-between" align="center" mb={8}>
        <Heading size="lg">Tags</Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={() => handleOpenModal()}
        >
          New Tag
        </Button>
      </Flex>

      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
        gap={6}
      >
        {tagsWithCount.map((tag) => (
          <Box
            key={tag.id}
            p={4}
            borderRadius="lg"
            bg={tagBgColor}
            shadow="base"
            borderLeft="4px solid"
            borderLeftColor={tag.color}
            cursor="pointer"
            onClick={() => handleTagClick(tag)}
            _hover={{ shadow: 'md' }}
          >
            <Flex justify="space-between" align="center">
              <VStack align="start" spacing={2}>
                <Text fontWeight="medium">{tag.decrypted_name}</Text>
                <Badge colorScheme="blue">{tag.entryCount} entries</Badge>
              </VStack>
              <HStack spacing={2}>
                <IconButton
                  aria-label="Edit tag"
                  icon={<FiEdit2 />}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModal(tag);
                  }}
                />
                <IconButton
                  aria-label="Delete tag"
                  icon={<FiTrash2 />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(tag.id);
                  }}
                />
              </HStack>
            </Flex>
          </Box>
        ))}
      </Grid>

      {selectedTag && (
        <Box mt={6}>
          <Flex align="center" mb={4}>
            <Box w={4} h={4} borderRadius="full" bg={selectedTag.color} mr={2} />
            <Text fontWeight="medium">{selectedTag.decrypted_name}</Text>
            <Badge colorScheme="blue" ml={2}>
              {selectedTag.entryCount} entries
            </Badge>
          </Flex>

          {loadingEntries ? (
            <Flex justify="center" align="center" h="100px">
              <Spinner />
            </Flex>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {tagEntries.map(entry => (
                <Box
                  key={entry.id}
                  p={4}
                  bg={tagBgColor}
                  borderRadius="md"
                  shadow="sm"
                  cursor="pointer"
                  onClick={() => handleEntryClick(entry)}
                  _hover={{ shadow: 'md' }}
                >
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    {format(new Date(entry.created_at), 'MMM d, yyyy')}
                  </Text>
                  <Text noOfLines={3}>
                    {decryptedPreviews[entry.id] || 'Error decrypting entry'}
                  </Text>
                  {entry.mood_score && (
                    <HStack spacing={1} mt={2}>
                      <Icon
                        as={entry.mood_score >= 60 ? FiSmile : entry.mood_score >= 40 ? FiMeh : FiFrown}
                        color={`hsl(${entry.mood_score * 1.2}, 70%, 50%)`}
                      />
                      <Text fontSize="xs" color={`hsl(${entry.mood_score * 1.2}, 70%, 50%)`}>
                        {entry.mood_label || (entry.mood_score >= 60 ? 'Happy' : entry.mood_score >= 40 ? 'Neutral' : 'Sad')}
                      </Text>
                    </HStack>
                  )}
                </Box>
              ))}
            </SimpleGrid>
          )}
        </Box>
      )}

      <Modal isOpen={entryModalOpen} onClose={() => setEntryModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>
            <Text fontSize="sm" color="gray.500">
              {selectedEntry && format(new Date(selectedEntry.created_at), 'MMMM d, yyyy')}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb={6}>
            {selectedEntry && (
              <Box>
                <Text whiteSpace="pre-wrap">
                  {decryptedPreviews[selectedEntry.id] || 'Error decrypting entry'}
                </Text>
                {selectedEntry.mood_score && (
                  <HStack spacing={2} mt={4}>
                    <Icon
                      as={selectedEntry.mood_score >= 60 ? FiSmile : selectedEntry.mood_score >= 40 ? FiMeh : FiFrown}
                      color={`hsl(${selectedEntry.mood_score * 1.2}, 70%, 50%)`}
                    />
                    <Text color={`hsl(${selectedEntry.mood_score * 1.2}, 70%, 50%)`}>
                      {selectedEntry.mood_label || (selectedEntry.mood_score >= 60 ? 'Happy' : selectedEntry.mood_score >= 40 ? 'Neutral' : 'Sad')}
                    </Text>
                  </HStack>
                )}
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingTagId ? 'Edit Tag' : 'Create New Tag'}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter tag name"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Color</FormLabel>
                <Input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
            >
              {editingTagId ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default TagsPage; 